import { ControlledTextInput } from '@components/ControlledTextInput';
import { CountrySelector } from '@components/CountrySelector';
import { useFF } from '@hooks/useFF.ts';
import type { FieldValues, Path } from 'react-hook-form';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';

type AddressGroupInputProps<T extends FieldValues> = {
	prefix: Path<T>;
	translatePrefix: string;
	required?: boolean;
	disabled?: boolean;
	requiredIfSelection?: boolean;
};

const NEW_ADDRESS = {
	country: null,
	country_sub_division: null,
	town: null,
	postal_code: null,
	line: null,
	building_number: null,
	building_name: null,
	street_name: null,
};

const OLD_ADDRESS = {
	country: null,
	line: null,
	town: null,
	building_number: null,
	postal_code: null,
};

export const AddressGroupInput = <T extends FieldValues>({
	prefix,
	translatePrefix,
	disabled,
	required,
	requiredIfSelection,
}: AddressGroupInputProps<T>) => {
	const hasNewAddress = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);
	const { control, watch, formState, setValue, clearErrors } = useFormContext<T>();
	const { t } = useTranslation('', { keyPrefix: translatePrefix });
	const { t: tCommon } = useTranslation();
	const isCountrySelected = !!watch(`${prefix}.country` as Path<T>);
	const requiredConditionally = requiredIfSelection && isCountrySelected;
	const isRequired = required || requiredConditionally;
	const errors = get(formState.errors, prefix);
	const clearAddress = () => {
		setValue(
			prefix as Path<T>,
			(hasNewAddress ? NEW_ADDRESS : OLD_ADDRESS) as T[Path<T>],
			{
				shouldValidate: false,
				shouldDirty: true,
				shouldTouch: true,
			},
		);
	};

	const handleCountryChange = (country?: string) => {
		if (!country) {
			clearErrors(prefix);
			clearAddress();
		} else {
			setValue(`${prefix}.country` as Path<T>, country as T[Path<T>]);
		}
	};

	return (
		<div className="space-y-space-16" data-test-id={prefix}>
			<Controller
				control={control}
				name={`${prefix}.country` as Path<T>}
				render={({ field: { onChange: _, ...field } }) => (
					<CountrySelector
						{...field}
						className="max-w-125"
						error={errors?.country?.message}
						isDisabled={disabled}
						isRequired={isRequired}
						label={t('country')}
						onChange={handleCountryChange}
						showClear
					/>
				)}
				rules={{ required: isRequired && tCommon('payments.newPayment.fieldIsRequired') }}
			/>
			{isCountrySelected && (
				<>
					{hasNewAddress && (
						<ControlledTextInput
							disabled={disabled}
							label={t('state')}
							name={`${prefix}.country_sub_division`}
							rules={{
								maxLength: {
									message: tCommon('payments.common.maxLength', { maxLength: 35 }),
									value: 35,
								},
							}}
						/>
					)}

					<ControlledTextInput
						disabled={disabled}
						label={t('town')}
						name={`${prefix}.town`}
						rules={{
							required: isRequired && tCommon('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: tCommon('payments.common.maxLength', { maxLength: 35 }),
								value: 35,
							},
						}}
					/>

					{hasNewAddress ? (
						<ControlledTextInput
							disabled={disabled}
							label={t('street')}
							name={`${prefix}.street_name`}
							rules={{
								maxLength: {
									message: tCommon('payments.common.maxLength', { maxLength: 70 }),
									value: 70,
								},
							}}
						/>
					) : (
						<ControlledTextInput
							disabled={disabled}
							label={t('street')}
							name={`${prefix}.line`}
							rules={{
								required: isRequired && tCommon('payments.newPayment.fieldIsRequired'),
							}}
						/>
					)}

					<ControlledTextInput
						disabled={disabled}
						label={t('buildingNumber')}
						name={`${prefix}.building_number`}
						rules={{
							required:
								!hasNewAddress &&
								isRequired &&
								tCommon('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: tCommon('payments.common.maxLength', { maxLength: 16 }),
								value: 16,
							},
						}}
					/>

					{hasNewAddress && (
						<ControlledTextInput
							disabled={disabled}
							label={t('buildingName')}
							name={`${prefix}.building_name`}
							rules={{
								maxLength: {
									message: tCommon('payments.common.maxLength', { maxLength: 35 }),
									value: 35,
								},
							}}
						/>
					)}

					<ControlledTextInput
						disabled={disabled}
						label={t('postalCode')}
						name={`${prefix}.postal_code`}
						rules={{
							required:
								!hasNewAddress &&
								isRequired &&
								tCommon('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: tCommon('payments.common.maxLength', { maxLength: 16 }),
								value: 16,
							},
						}}
					/>
				</>
			)}
		</div>
	);
};
